import { Component, OnInit, Output, EventEmitter, Input, ViewChild, ElementRef } from '@angular/core';
import { TaskDataService } from '../../../shared/services/task-data.service';
import {ITreasuryRequestData,TreasuryRequestData, ITaskRequest, TaskRequest, ILoanBorrower } from '../../../modules/tasks/model/types';
import notify from 'devextreme/ui/notify';
import validationEngine from "devextreme/ui/validation_engine";
import { TreasuryDataService } from '../../../shared/services/treasury-data.service';
import { StaticDataService } from '../../../shared/services/static-data.service';
import { getNotificationTimeout, sendNotificationMessage } from '../../../types/resource';
import { LoggingService } from '../../../framework/logging/logging.service';
import { SeverityLevel } from '../../../framework/logging/severity-level.model';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { MessageService } from '../../../shared/components/framework/services/message.service';
import { FormUtils } from '../../../shared/components/framework/services/form-builder.service';
import { LoanDataBase } from '../baseclass/loandata-base';
import { FeatureFlagsService } from '../../../shared/services/feature-flags.service';
import { UtilitiesService } from '../../../shared/components/framework/services/utilities.service';
import dxDataGrid from 'devextreme/ui/data_grid';
import { IAccount } from '../../../modules/deal/model/types';
import { FileUploadComponent } from '../../../shared/components/dynamic-form-widgets/file-upload/file-upload.component';

@Component({
  standalone: false,
  selector: 'app-treasury-request',
  templateUrl: './treasury-request.component.html',
  styleUrls: ['./treasury-request.component.scss']
})
export class TreasuryRequestComponent extends LoanDataBase implements OnInit {
  @ViewChild('formElement') formElement!: ElementRef;
  @ViewChild(FileUploadComponent) fileUploadComponent?: FileUploadComponent;

  @Input() taskTemplateName: string;
  @Input() task: ITaskRequest;

  @Output() onAddNewTaskEventEmitter: EventEmitter<any> = new EventEmitter();

  private bankAccountsSubject$ = new BehaviorSubject<IAccount[]>([]);
  bankAccounts$ = this.bankAccountsSubject$.asObservable();
  private loanBorrowersSubject$ = new BehaviorSubject<ILoanBorrower[]>([]);
  loanBorrowers$ = this.loanBorrowersSubject$.asObservable();
  isGridInEditMode: boolean = false;
  currentFormInstance: any = null;
  bankAccountsGrid: dxDataGrid | null = null;
  formColCountBehaviourSubject$ = new BehaviorSubject<number>(2);
  formColCount$ = this.formColCountBehaviourSubject$.asObservable();
  formColCount: number = 2;
  formItems: any[] = [];
  requestData: any = {};
  treasuryRequestTypes: any[];
  bankData: any[];
  treasuryRequestData: ITreasuryRequestData;
  isLoading: boolean = false;
  files: File[] = [];
  requestTypes: any[];
  isEditable: boolean = false;
  currentRequestType: string = "";
  mandatoryFiles: boolean = false;
  override showInactiveLoans: boolean = false;
  formItemsBehaviourSubject$ = new BehaviorSubject<any[]>([]);
  formItems$ = this.formItemsBehaviourSubject$.asObservable();
  isUrgent: boolean = false;
  isInterestBearing: boolean = false;
  isComingled: boolean = false;
  accountNotOpenedByMs: boolean = false;
  reserveTypes: any[] = [];
  isCustomLoanNumber: boolean = false;
  originalLoanNumbers: Set<string> = new Set();

  /** Clears the dropdown's search filter so reopening it shows the full list, not just the selected item. */
  onRequestTypeDropdownOpened = (e: any): void => {
    const dataSource = e.component.getDataSource();
    if (dataSource) {
      dataSource.searchValue('');
      dataSource.load();
    }
  };
  borrowerNameReadOnly: boolean = true;
  dealNameReadOnly: boolean = true;
  accountTypes: any[] = ["Collection Account", "Master Escrow/Reserve", "Escrow", "Reserve", "Temporary CMA Holding Account"];

  constructor(
    taskDataService: TaskDataService,
    private readonly treasuryDataService: TreasuryDataService,
    private readonly loggingService: LoggingService,
    private readonly messageService: MessageService,
    private readonly staticDataService: StaticDataService,
    featureFlagsService: FeatureFlagsService,
  ) {
    super(taskDataService, featureFlagsService);
    this.onRequestTypeSelectionChanged =
      this.onRequestTypeSelectionChanged.bind(this);
    this.onLoanNumberChanged = this.onLoanNumberChanged.bind(this);
    this.onIsComingledChanged = this.onIsComingledChanged.bind(this);
    this.onReserveBucketsChanged = this.onReserveBucketsChanged.bind(this);
    this.onAccountTypeChanged = this.onAccountTypeChanged.bind(this);
    this.formItems$.subscribe((items: any[]) => {
      this.formItems = items;
    });

    this.formColCount$.subscribe((colcount: number) => {
      this.formColCount = colcount;
    });

    // Subscribe to deal data changes to update loan numbers display when editing
    this.dealData$.subscribe((data) => {
      // Always update the local dealData property
      this.dealData = data;
    });

    // Subscribe to loan data changes and refresh form configuration
    this.loanNumbers$.subscribe((data) => {
      // Always update the local loanData property
      this.loanNumbers = data;

      // Store original loan numbers for custom loan detection
      this.originalLoanNumbers.clear();
      data.forEach(loan => {
        if (loan.name) {
          this.originalLoanNumbers.add(loan.name);
        }
      });

      this.updateLoanDataInFormConfig();
    });

  }

    private updateLoanDataInFormConfig(): void {
    if (!this.formItems || this.formItems.length === 0) {
      return;
    }

    // Don't deep clone - work with the original reference to preserve functions
    this.updateLoanDataRecursively(this.formItems);

    // Emit the updated configuration
    this.formItemsBehaviourSubject$.next([...this.formItems]);
  }

  private updateLoanDataRecursively(items: any[]): void {
    for (const item of items) {
      // Check if this is a loan select box
      if (
        item.dataField === "loanNumber" &&
        item.editorType === "dxSelectBox"
      ) {
        // Update only the items, preserve all other editorOptions including itemTemplate
        if (item.editorOptions) {
          item.editorOptions.items = this.loanNumbers;
          // Ensure itemTemplate is preserved
          if (!item.editorOptions.itemTemplate) {
            item.editorOptions.itemTemplate = this.loanItemTemplate;
          }
          // Restore the onValueChanged function reference that was lost during JSON serialization
          if (!item.editorOptions.onValueChanged) {
            item.editorOptions.onValueChanged = this.onLoanNumberChanged;
          }
        }
      }

      // Check if this is a checkbox for showing inactive loans
      if (
        item.editorType === "dxCheckBox" &&
        item.cssClass === "loan-checkbox-center-align"
      ) {
        if (item.editorOptions) {
          item.editorOptions.value = this.showInactiveLoans;
          item.editorOptions.text = this.activeLoanCheckboxText;
          // Restore the function reference
          if (!item.editorOptions.onValueChanged) {
            item.editorOptions.onValueChanged = this.onShowInactiveLoansChanged;
          }
        }
      }

      // Check if this is the isComingled checkbox
      if (
        item.dataField === "isComingled" &&
        item.editorType === "dxCheckBox"
      ) {
        if (item.editorOptions) {
          // Restore the function reference
          if (!item.editorOptions.onValueChanged) {
            item.editorOptions.onValueChanged = this.onIsComingledChanged;
          }
        }
      }

      // Check if this is the accountType TagBox
      if (
        item.dataField === "accountType" &&
        item.editorType === "dxTagBox"
      ) {
        if (item.editorOptions) {
          // Restore the items array
          item.editorOptions.items = this.accountTypes;
          // Restore the onValueChanged function reference
          if (!item.editorOptions.onValueChanged) {
            item.editorOptions.onValueChanged = this.onAccountTypeChanged;
          }
          // Pre-populate selected values when editing
          if (this.requestData.accountType && Array.isArray(this.requestData.accountType)) {
            item.editorOptions.value = this.requestData.accountType;
          }
        }
      }

      // Check if this is the accountTypeDisplay read-only text box
      if (
        item.dataField === "accountTypeDisplay" &&
        item.editorType === "dxTextBox"
      ) {
        if (item.editorOptions && this.requestData.accountTypeDisplay) {
          item.editorOptions.value = this.requestData.accountTypeDisplay;
        }
      }

      // Check if this is the reserveBuckets TagBox
      if (
        item.dataField === "reserveBuckets" &&
        item.editorType === "dxTagBox"
      ) {
        if (item.editorOptions) {
          // Restore the items array
          item.editorOptions.items = this.reserveTypes;
          // Restore the onValueChanged function reference
          if (!item.editorOptions.onValueChanged) {
            item.editorOptions.onValueChanged = this.onReserveBucketsChanged;
          }
          // Pre-populate selected values when editing
          if (this.requestData.reserveBuckets && Array.isArray(this.requestData.reserveBuckets)) {
            item.editorOptions.value = this.requestData.reserveBuckets;
          }
        }
      }

      // Check if this is the reserveBucketsDisplay read-only text box
      if (
        item.dataField === "reserveBucketsDisplay" &&
        item.editorType === "dxTextBox"
      ) {
        if (item.editorOptions && this.requestData.reserveBucketsDisplay) {
          item.editorOptions.value = this.requestData.reserveBucketsDisplay;
        }
      }

      // Check if this is a group with items
      if (item.itemType === "group" && item.items) {
        this.updateLoanDataRecursively(item.items);
      }

      // Also check for nested items in other structures
      if (item.items && Array.isArray(item.items)) {
        this.updateLoanDataRecursively(item.items);
      }
    }
  }

  onLoanNumberChanged(e: any): void {
    if (e.value) {
      // Check if the loan number exists in the original loaded loan numbers
      this.isCustomLoanNumber = !this.originalLoanNumbers.has(e.value);

      console.log('onLoanNumberChanged:', {
        value: e.value,
        isCustom: this.isCustomLoanNumber,
        originalLoansCount: this.originalLoanNumbers.size
      });

      // Find the selected loan object in the original loanData array
      const selectedLoan = this.loanNumbers.find(loan => loan.name === e.value);

      let borrowerName = '';
      let dealName = '';

      if (!this.isCustomLoanNumber && selectedLoan) {
        // Auto-populate from the selected loan object
        // Get dealName directly from the loan object
        dealName = selectedLoan.dealName || '';

        // If dealName is not available in the loan object, look it up in dealData
        if (!dealName && this.dealData && e.value) {
          // Extract deal ID from loan number using the established convention:
          // Split on ' - ' first, then take first 6 chars
          const loanParts = e.value.split(' - ');
          const dealId = loanParts[0].substring(0, 6);

          // Find the deal in dealData by matching the first 6 chars of the deal name
          const matchingDeal = this.dealData.find(deal =>
            deal.name && deal.name.substring(0, 6) === dealId
          );

          if (matchingDeal) {
            dealName = matchingDeal.name;
          }
        }

        // Get borrower name from borrowPower property or fall back to loanBorrowers lookup
        if (selectedLoan.borrowPower) {
          borrowerName = selectedLoan.borrowPower;
        } else {
          // Fallback 1: match by comparing first part of loan number (before dash)
          const loanBorrower = this.loanBorrowersSubject$.value.find(
            lb => lb.loanNumber.split('-')[0].trim() === e.value.split('-')[0].trim()
          );
          borrowerName = loanBorrower ? loanBorrower.borrower : '';

          // Fallback 2: search allLoanData (from getLoanNumbersWithDeal) which has
          // borrowPower for all loans including inactive ones
          if (!borrowerName && this.allLoanData) {
            const fullLoan = this.allLoanData.find(l => l.name === e.value);
            if (fullLoan && fullLoan.borrowPower) {
              borrowerName = fullLoan.borrowPower;
            }
          }
        }
      }
      // If custom loan number, leave borrowerName and dealName empty

      // Update form data - use setTimeout to ensure form instance is ready
      setTimeout(() => {
        if (this.currentFormInstance) {
          this.currentFormInstance.updateData('borrowerName', borrowerName);
          this.currentFormInstance.updateData('dealName', dealName);

          // Get the borrowerName item editor
          const borrowerNameEditor = this.currentFormInstance.getEditor('borrowerName');

          if (borrowerNameEditor) {
            console.log('Updating borrowerName readOnly to:', !this.isCustomLoanNumber);

            // Update readOnly state (both form config + DOM) based on whether loan is custom
            this.setFieldReadOnlyState('borrowerName', !this.isCustomLoanNumber);

            const borrowerValidationRules = this.isCustomLoanNumber
              ? [{ type: 'required', message: 'Borrower Name is required' }]
              : [];

            try {
              this.currentFormInstance.itemOption('borrowerName', 'validationRules', borrowerValidationRules);
            } catch (validationError) {
              console.warn('Unable to update borrowerName validation rules:', validationError);
            }
          }

          // Do the same for dealName if it exists
          const dealNameEditor = this.currentFormInstance.getEditor('dealName');
          if (dealNameEditor) {
            this.setFieldReadOnlyState('dealName', !this.isCustomLoanNumber);

            const dealValidationRules = this.isCustomLoanNumber
              ? [{ type: 'required', message: 'Deal Name is required' }]
              : [];

            try {
              this.currentFormInstance.itemOption('dealName', 'validationRules', dealValidationRules);
            } catch (validationError) {
              console.warn('Unable to update dealName validation rules:', validationError);
            }
          }

          // Restore display fields for TagBox values
          if (this.requestData.accountType && Array.isArray(this.requestData.accountType)) {
            const accountTypeDisplay = this.requestData.accountType.join(', ');
            this.requestData.accountTypeDisplay = accountTypeDisplay;
            this.currentFormInstance.updateData('accountTypeDisplay', accountTypeDisplay);
          }

          if (this.requestData.reserveBuckets && Array.isArray(this.requestData.reserveBuckets)) {
            const reserveBucketsDisplay = this.requestData.reserveBuckets.join(', ');
            this.requestData.reserveBucketsDisplay = reserveBucketsDisplay;
            this.currentFormInstance.updateData('reserveBucketsDisplay', reserveBucketsDisplay);
          }
        } else {
          // Fallback: update requestData directly
          this.requestData.borrowerName = borrowerName;
          this.requestData.dealName = dealName;
        }
      }, 0);
    } else {
      // If value is empty, also clear the fields
      setTimeout(() => {
        if (this.currentFormInstance) {
          this.currentFormInstance.updateData('borrowerName', '');
          this.currentFormInstance.updateData('dealName', '');
          this.setFieldReadOnlyState('borrowerName', false);
          this.setFieldReadOnlyState('dealName', false);
        }
      }, 0);
    }
  }

  onIsComingledChanged(e: any): void {
    this.isComingled = e.value;
  }

  onReserveBucketsChanged(e: any): void {
    // Store the array value directly in requestData
    this.requestData.reserveBuckets = e.value || [];
    // Update the display field with comma-delimited string
    const displayValue = (e.value || []).join(', ');
    this.requestData.reserveBucketsDisplay = displayValue;
    // Force update the display field
    setTimeout(() => {
      if (this.currentFormInstance) {
        this.currentFormInstance.updateData("reserveBucketsDisplay", displayValue);
      }
    }, 0);
  }

  onAccountTypeChanged(e: any): void {
    // Store the array value directly in requestData
    this.requestData.accountType = e.value || [];
    // Update the display field with comma-delimited string
    const displayValue = (e.value || []).join(', ');
    this.requestData.accountTypeDisplay = displayValue;
    // Force update the display field
    setTimeout(() => {
      if (this.currentFormInstance) {
        this.currentFormInstance.updateData("accountTypeDisplay", displayValue);
      }
    }, 0);
  }

  onFileUpload(file: File) {
    this.files.push(file);
  }

  onFileRemove(file: File)
   {
    if(file != null && file != undefined)
    {
      this.files = this.files.filter(f => f !== file);
    }
   }

  /** Clears all user-entered state so no data from the previous request type carries over. */
  private resetFormStateForRequestTypeChange(): void {
    this.requestData = {};
    this.files = [];
    this.fileUploadComponent?.clearAllFiles();
    this.mandatoryFiles = false;
    this.isUrgent = false;
    this.isInterestBearing = false;
    this.isComingled = false;
    this.accountNotOpenedByMs = false;
    this.isCustomLoanNumber = false;
    this.isGridInEditMode = false;
    this.showInactiveLoans = false;
    this.originalLoanNumbers = new Set();
    validationEngine.resetGroup("formGroup");
  }

  onRequestTypeSelectionChanged(e: any) {
    // Safety check for selectedItem
    if (!e.selectedItem || !e.selectedItem.name) {
      console.warn('Invalid selection event:', e);
      return;
    }

    const previousRequestType = this.currentRequestType;
    const newRequestType = e.selectedItem.name;
    const isActualChange =
      !this.isEditable &&
      !!previousRequestType &&
      previousRequestType !== newRequestType;

    if (isActualChange) {
      this.resetFormStateForRequestTypeChange();
    }

    this.currentRequestType = newRequestType;
    this.requestData.requestType = newRequestType;
    // Reset read-only state when switching request types
    this.borrowerNameReadOnly = true;
    this.dealNameReadOnly = true;

    // Reset accountTypes to base list (without CMA)
    this.accountTypes = ["Collection Account", "Master Escrow/Reserve", "Escrow", "Reserve", "Temporary CMA Holding Account"];

    let config = [];
    switch (e.selectedItem.name.toUpperCase()) {
    case "ACCOUNT RECON INQUIRY":
      this.mandatoryFiles = !this.isEditable;
      config = [
        {
          dataField: "requestType",
          editorType: "dxTextBox",
          label: { text: "Request Type" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { value: e.selectedItem.name, readOnly: true, required: "true" },
          validationRules: [
            { type: "required", message: "Request Type is required" },
          ],
        },
        {
          itemType: "group",
          colCount: 4,
          colSpan: 2,
          items: [
            {
              labelMode: "static",
              dataField: "loanNumber",
              editorType: "dxSelectBox",
              colSpan: 3,
              cssClass: "dx-widget",
              label: { text: "Loan Number" },
              editorOptions: {
                items: this.loanNumbers,
                valueExpr: "name",
                displayExpr: "name",
                required: false,
                searchMode: "contains",
                searchEnabled: true,
                acceptCustomValue: true,
                onCustomItemCreating: (e) => ({ name: e.text }),
                itemTemplate: this.loanItemTemplate,
              },
            },
            {
              editorType: "dxCheckBox",
              label: { visible: false },
              cssClass: "loan-checkbox-center-align",
              editorOptions: {
                text: this.activeLoanCheckboxText,
                onValueChanged: this.onShowInactiveLoansChanged,
                value: this.showInactiveLoans,
              },
            },
          ],
          validationRules: [
            { type: "required", message: "Loan Number is required" },
          ],
        },
        {
          itemType: "custom",
          name: "bankAccountsGrid",
          colSpan: 2,
          template: (formItemData, container) => this.bankAccountGridTemplate(formItemData, container)
        },
        {
          // work around to bind bank accounts grid to form and validate it
          dataField: "bankAccountsProxy",
          colSpan: 2,
          itemType: "simple",
          label: { visible: false },
          editorType: "dxTextBox",
          editorOptions: {
            visible: false,
            value: this.requestData.bankAccounts?.filter(a => a.accountName).length.toString() || "0",
          },
          validationRules: [
            {
              type: "custom",
              message: "At least one bank account is required",
              validationCallback: (e) => this.onValidatebankAccounts(e),
              reevaluate: true,
            },
          ],
        },
        {
          colSpan: 2,
          dataField: "isUrgent",
          editorType: "dxCheckBox",
          label: { text: "Rush Job?" },
          editorOptions: {
            text: "",
            onValueChanged: (e: any) => {
              this.isUrgent = e.value;
            }
          }
        },
        {
          dataField: "description",
          editorType: "dxTextArea",
          label: { text: "Description" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: {
            required: "true",
            defaultValue: "",
            height: 100,
          },
          validationRules: [
            { type: "required", message: "Description is required" },
          ],
        },

        {
          colSpan: 2,
          horizontalAlignment: "center",
          itemType: "simple",
          name: "fileUpload",
          label: {
            text: this.mandatoryFiles ? "Upload Files *" : "Upload Files",
          },
          template: "fileUploadTemplate",
        },
        {
          // work around to bind file upload component to form and validate it
          dataField: "fileUploadProxy",
          colSpan: 2,
          itemType: "simple",
          label: { visible: false },
          editorType: "dxTextBox",
          editorOptions: {
            visible: false,
            value: this.files.length.toString(),
          },
          validationRules: [
            {
              type: "custom",
              message: "At least one file is required",
              validationCallback: (e) => this.onValidateFileAttachments(e),
              reevaluate: true,
            },
          ],
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          cssClass: "borrow-submit",
          editorType: "dxButton",
          editorOptions: { text: "Submit", useSubmitBehavior: true },
        },
      ];
      break;
    case "BANK ACCOUNT CLOSE":
      this.mandatoryFiles = !this.isEditable;
      // Initialize accountType as an empty array for the TagBox
      if (!this.requestData.accountType) {
        this.requestData.accountType = [];
      }
      config = [
        {
          dataField: "requestType",
          editorType: "dxTextBox",
          label: { text: "Request Type" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { value: e.selectedItem.name, readOnly: true, required: "true" },
          validationRules: [
            { type: "required", message: "Request Type is required" },
          ],
        },
        {
          itemType: "group",
          colCount: 4,
          colSpan: 2,
          items: [
            {
              labelMode: "static",
              dataField: "loanNumber",
              editorType: "dxSelectBox",
              colSpan: 3,
              cssClass: "dx-widget",
              label: { text: "Loan Number" },
              editorOptions: {
                items: this.loanNumbers,
                valueExpr: "name",
                displayExpr: "name",
                required: true,
                searchMode: "contains",
                searchEnabled: true,
                acceptCustomValue: true,
                onCustomItemCreating: (e) => ({ name: e.text }),
                itemTemplate: this.loanItemTemplate,
                onValueChanged: this.onLoanNumberChanged,
              },
              validationRules: [
                { type: "required", message: "Loan Number is required" },
              ],
            },
            {
              editorType: "dxCheckBox",
              label: { visible: false },
              cssClass: "loan-checkbox-center-align",
              editorOptions: {
                text: this.activeLoanCheckboxText,
                onValueChanged: this.onShowInactiveLoansChanged,
                value: this.showInactiveLoans,
              },
            },
          ],
        },
        {
          itemType: "group",
          colCount: 4,
          colSpan: 2,
          items: [
            {
              labelMode: "static",
              dataField: "accountType",
              editorType: "dxTagBox",
              colSpan: 4,
              cssClass: "dx-widget",
              label: { text: "Account Type" },
              editorOptions: {
                items: this.accountTypes,
                searchEnabled: true,
                showSelectionControls: true,
                applyValueMode: "useButtons",
                multiline: true,
                elementAttr: {
                  "data-testid": "account-type-tagbox"
                },
                onValueChanged: this.onAccountTypeChanged
              },
              validationRules: [
                { type: "required", message: "Account Type is required" },
              ],
            },
          ]
        },
        {
          dataField: "accountTypeDisplay",
          editorType: "dxTextBox",
          label: { text: "Selected Account Types" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: {
            readOnly: true,
            value: "",
            elementAttr: {
              "data-testid": "account-type-display"
            }
          },
        },
        {
          dataField: "borrowerName",
          editorType: "dxTextBox",
          label: { text: "Borrower Name" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", readOnly: this.borrowerNameReadOnly },
        },
        {
          itemType: "custom",
          name: "bankAccountsGrid",
          colSpan: 2,
          template: (formItemData, container) => this.bankAccountGridTemplate(formItemData, container)
        },
        {
          // work around to bind bank accounts grid to form and validate it
          dataField: "bankAccountsProxy",
          colSpan: 2,
          itemType: "simple",
          label: { visible: false },
          editorType: "dxTextBox",
          editorOptions: {
            visible: false,
            value: this.requestData.bankAccounts?.filter(a => a.accountName).length.toString() || "0",
          },
          validationRules: [
            {
              type: "custom",
              message: "At least one bank account is required",
              validationCallback: (e) => this.onValidatebankAccounts(e),
              reevaluate: true,
            },
          ],
        },
        {
          colSpan: 2,
          dataField: "isUrgent",
          editorType: "dxCheckBox",
          label: { text: "Rush Job?" },
          editorOptions: {
            text: "",
            onValueChanged: (e: any) => {
              this.isUrgent = e.value;
            }
          }
        },
        {
          dataField: "description",
          editorType: "dxTextArea",
          label: { text: "Description" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", height: 100 },
          validationRules: [
            { type: "required", message: "Description is required" },
          ],
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          itemType: "simple",
          name: "fileUpload",
          label: {
            text: this.mandatoryFiles ? "Upload Files *" : "Upload Files",
          },
          template: "fileUploadTemplate",
        },
        {
          // work around to bind file upload component to form and validate it
          dataField: "fileUploadProxy",
          colSpan: 2,
          itemType: "simple",
          label: { visible: false },
          editorType: "dxTextBox",
          editorOptions: {
            visible: false,
            value: this.files.length.toString(),
          },
          validationRules: [
            {
              type: "custom",
              message: "At least one file is required",
              validationCallback: (e) => this.onValidateFileAttachments(e),
              reevaluate: true,
            },
          ],
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          cssClass: "borrow-submit",
          editorType: "dxButton",
          editorOptions: { text: "Submit", useSubmitBehavior: true },
        },
      ];
      break;
    case "ACCOUNT RECONCILIATION":
    case "BANK ACCESS REQUEST":
    case "BANK STATEMENT REQUEST":
    case "REMITTANCE":
    case "BANK TRACKER UPDATE":
      this.mandatoryFiles = !this.isEditable;
      config = [
        {
          dataField: "requestType",
          editorType: "dxTextBox",
          label: { text: "Request Type" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { value: e.selectedItem.name, readOnly: true, required: "true" },
          validationRules: [
            { type: "required", message: "Request Type is required" },
          ],
        },
        {
          itemType: "group",
          colCount: 4,
          colSpan: 2,
          items: [
            {
              labelMode: "static",
              dataField: "loanNumber",
              editorType: "dxSelectBox",
              colSpan: 3,
              cssClass: "dx-widget",
              label: { text: "Loan Number" },
              editorOptions: {
                items: this.loanNumbers,
                valueExpr: "name",
                displayExpr: "name",
                required: true,
                searchMode: "contains",
                searchEnabled: true,
                acceptCustomValue: true,
                onCustomItemCreating: (e) => ({ name: e.text }),
                itemTemplate: this.loanItemTemplate,
                onValueChanged: this.onLoanNumberChanged,
              },
              validationRules: [
                { type: "required", message: "Loan Number is required" },
              ],
            },
            {
              editorType: "dxCheckBox",
              label: { visible: false },
              cssClass: "loan-checkbox-center-align",
              editorOptions: {
                text: this.activeLoanCheckboxText,
                onValueChanged: this.onShowInactiveLoansChanged,
                value: this.showInactiveLoans,
              },
            },
          ],
        },
        {
          dataField: "borrowerName",
          editorType: "dxTextBox",
          label: { text: "Borrower Name" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: {
            required: "false",
            defaultValue: "",
            readOnly: this.borrowerNameReadOnly,
          },
        },
        {
          itemType: "custom",
          name: "bankAccountsGrid",
          colSpan: 2,
          template: (formItemData, container) => this.bankAccountGridTemplate(formItemData, container)
        },
        {
          // work around to bind bank accounts grid to form and validate it
          dataField: "bankAccountsProxy",
          colSpan: 2,
          itemType: "simple",
          label: { visible: false },
          editorType: "dxTextBox",
          editorOptions: {
            visible: false,
            value: this.requestData.bankAccounts?.filter(a => a.accountName).length.toString() || "0",
          },
          validationRules: [
            {
              type: "custom",
              message: "At least one bank account is required",
              validationCallback: (e) => this.onValidatebankAccounts(e),
              reevaluate: true,
            },
          ],
        },
        {
          colSpan: 2,
          dataField: "isUrgent",
          editorType: "dxCheckBox",
          label: { text: "Rush Job?" },
          editorOptions: {
            text: "",
            onValueChanged: (e: any) => {
              this.isUrgent = e.value;
            }
          }
        },
        {
          dataField: "description",
          editorType: "dxTextArea",
          label: { text: "Description" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", height: 100 },
          validationRules: [
            { type: "required", message: "Description is required" },
          ],
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          itemType: "simple",
          name: "fileUpload",
          label: {
            text: this.mandatoryFiles ? "Upload Files *" : "Upload Files",
          },
          template: "fileUploadTemplate",
        },
        {
          // work around to bind file upload component to form and validate it
          dataField: "fileUploadProxy",
          colSpan: 2,
          itemType: "simple",
          label: { visible: false },
          editorType: "dxTextBox",
          editorOptions: {
            visible: false,
            value: this.files.length.toString(),
          },
          validationRules: [
            {
              type: "custom",
              message: "At least one file is required",
              validationCallback: (e) => this.onValidateFileAttachments(e),
              reevaluate: true,
            },
          ],
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          cssClass: "borrow-submit",
          editorType: "dxButton",
          editorOptions: { text: "Submit", useSubmitBehavior: true },
        },
      ];
      break;
    case "BANK ACCOUNT OPEN":
    case "BANK ACCOUNT NAME CHANGE":
      this.mandatoryFiles = !this.isEditable;
      // Initialize accountType as an empty array for the TagBox
      if (!this.requestData.accountType) {
        this.requestData.accountType = [];
      }
      config = [
        {
          dataField: "requestType",
          editorType: "dxTextBox",
          label: { text: "Request Type" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { value: e.selectedItem.name, readOnly: true, required: "true" },
          validationRules: [
            { type: "required", message: "Request Type is required" },
          ],
        },
        {
          itemType: "group",
          colCount: 4,
          colSpan: 2,
          items: [
            {
              labelMode: "static",
              dataField: "loanNumber",
              editorType: "dxSelectBox",
              colSpan: 3,
              cssClass: "dx-widget",
              label: { text: "Loan Number" },
              editorOptions: {
                items: this.loanNumbers,
                valueExpr: "name",
                displayExpr: "name",
                required: true,
                searchMode: "contains",
                searchEnabled: true,
                acceptCustomValue: true,
                onCustomItemCreating: (e) => ({ name: e.text }),
                itemTemplate: this.loanItemTemplate,
                onValueChanged: this.onLoanNumberChanged,
              },
              validationRules: [
                { type: "required", message: "Loan Number is required" },
              ],
            },
            {
              editorType: "dxCheckBox",
              label: { visible: false },
              cssClass: "loan-checkbox-center-align",
              editorOptions: {
                text: this.activeLoanCheckboxText,
                onValueChanged: this.onShowInactiveLoansChanged,
                value: this.showInactiveLoans,
              },
            },
          ],
        },
        {
          itemType: "group",
          colCount: 4,
          colSpan: 2,
          items: [
            {
              labelMode: "static",
              dataField: "accountType",
              editorType: "dxTagBox",
              colSpan: 4,
              cssClass: "dx-widget",
              label: { text: "Account Type" },
              editorOptions: {
                items: this.accountTypes,
                searchEnabled: true,
                showSelectionControls: true,
                applyValueMode: "useButtons",
                multiline: true,
                elementAttr: {
                  "data-testid": "account-type-tagbox"
                },
                onValueChanged: this.onAccountTypeChanged
              },
              validationRules: [
                { type: "required", message: "Account Type is required" },
              ],
            },
          ]
        },
        {
          dataField: "accountTypeDisplay",
          editorType: "dxTextBox",
          label: { text: "Selected Account Types" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: {
            readOnly: true,
            value: "",
            elementAttr: {
              "data-testid": "account-type-display"
            }
          },
        },
        {
          itemType: "group",
          colCount: 4,
          colSpan: 2,
          items: [
            {
              dataField: "borrowerName",
              editorType: "dxTextBox",
              label: { text: "Borrower Name" },
              colSpan: 4,
              cssClass: "dx-widget",
              editorOptions: { required: "true", defaultValue: "", readOnly: this.borrowerNameReadOnly },
            },
          ]
        },
        {
          itemType: "group",
          colCount: 2,
          colSpan: 2,
          items: [
            {
              colSpan: 1,
              dataField: "isInterestBearing",
              editorType: "dxCheckBox",
              label: { text: "Interest Bearing Account Requested" },
              editorOptions: {
                text: "",
                onValueChanged: (e: any) => {
                  this.isInterestBearing = e.value;
                }
              }
            },
            {
              colSpan: 1,
              dataField: "accountNotOpenedByMs",
              editorType: "dxCheckBox",
              label: { text: "Account Not Opened By MS" },
              editorOptions: {
                text: "",
                onValueChanged: (e: any) => {
                  this.accountNotOpenedByMs = e.value;
                }
              }
            },
          ]
        },
        {
          colSpan: 2,
          dataField: "isUrgent",
          editorType: "dxCheckBox",
          label: { text: "Rush Job" },
          editorOptions: {
            text: "",
            onValueChanged: (e: any) => {
              this.isUrgent = e.value;
            }
          }
        },
        {
          dataField: "description",
          editorType: "dxTextArea",
          label: { text: "Description" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", height: 100 },
          validationRules: [
            { type: "required", message: "Description is required" },
          ],
        },
        {
          itemType: "simple",
          colSpan: 2,
          label: { text: "W9 & New BA Request Form are required attachments" },
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          itemType: "simple",
          name: "fileUpload",
          label: {
            text: this.mandatoryFiles ? "Upload Files *" : "Upload Files",
          },
          template: "fileUploadTemplate",
        },
        {
          // work around to bind file upload component to form and validate it
          dataField: "fileUploadProxy",
          colSpan: 2,
          itemType: "simple",
          label: { visible: false },
          editorType: "dxTextBox",
          editorOptions: {
            visible: false,
            value: this.files.length.toString(),
          },
          validationRules: [
            {
              type: "custom",
              message: "At least one file is required",
              validationCallback: (e) => this.onValidateFileAttachments(e),
              reevaluate: true,
            },
          ],
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          cssClass: "borrow-submit",
          editorType: "dxButton",
          editorOptions: { text: "Submit", useSubmitBehavior: true },
        },
      ];
      break;

    case "REMOVE BANK ACCOUNT FROM INTERNAL SYSTEMS":
      this.mandatoryFiles = false;
      config = [
        {
          dataField: "requestType",
          editorType: "dxTextBox",
          label: { text: "Request Type" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { value: e.selectedItem.name, readOnly: true, required: "true" },
          validationRules: [
            { type: "required", message: "Request Type is required" },
          ],
        },
        {
          itemType: "custom",
          name: "bankAccountsGrid",
          colSpan: 2,
          template: (formItemData, container) => this.bankAccountGridTemplate(formItemData, container)
        },
        {
          dataField: "accountName",
          editorType: "dxTextBox",
          label: { text: "Account Name" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", readOnly: true },
          validationRules: [
            { type: "required", message: "Account Name is required" },
          ],
        },
        {
          dataField: "accountNumber",
          editorType: "dxTextBox",
          label: { text: "Account Number" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", readOnly: true },
          validationRules: [
            { type: "required", message: "Account Number is required" },
          ],
        },
        {
          dataField: "bankName",
          editorType: "dxTextBox",
          label: { text: "Bank Name" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", readOnly: true },
          validationRules: [
            { type: "required", message: "Bank Name is required" },
          ],
        },
        {
          itemType: "group",
          colCount: 4,
          colSpan: 2,
          items: [
            {
              labelMode: "static",
              dataField: "loanNumber",
              editorType: "dxSelectBox",
              colSpan: 3,
              cssClass: "dx-widget",
              label: { text: "Loan Number" },
              editorOptions: {
                  items: this.loanNumbers,
                  valueExpr: "name",
                  displayExpr: "name",
                  required: true,
                  searchMode: "contains",
                  searchEnabled: true,
                  acceptCustomValue: true,
                  onCustomItemCreating: (e) => {
                    const newItem = { name: e.text };
                    this.loanNumbers.push(newItem);
                    return newItem;
                  },
                  itemTemplate: this.loanItemTemplate,
                  onValueChanged: this.onLoanNumberChanged,
              },
              validationRules: [
                { type: "required", message: "Loan Number is required" },
              ],
            },
            {
              editorType: "dxCheckBox",
              label: { visible: false },
              cssClass: "loan-checkbox-center-align",
              editorOptions: {
                text: this.activeLoanCheckboxText,
                onValueChanged: this.onShowInactiveLoansChanged,
                value: this.showInactiveLoans,
              },
            },
          ],
        },
        {
          dataField: "dealName",
          editorType: "dxTextBox",
          label: { text: "Deal Name" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "false", defaultValue: "", readOnly: this.borrowerNameReadOnly },
        },
        {
          dataField: "borrowerName",
          editorType: "dxTextBox",
          label: { text: "Borrower Name" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "false", defaultValue: "", readOnly: this.borrowerNameReadOnly },
        },
        {
          colSpan: 2,
          dataField: "isComingled",
          editorType: "dxCheckBox",
          label: { text: "Is the Account Comingled?" },
          editorOptions: {
            text: "",
            onValueChanged: this.onIsComingledChanged
          }
        },
        {
          colSpan: 2,
          dataField: "isUrgent",
          editorType: "dxCheckBox",
          label: { text: "Rush Job" },
          editorOptions: {
            text: "",
            onValueChanged: (e: any) => {
              this.isUrgent = e.value;
            }
          }
        },
        {
          dataField: "description",
          editorType: "dxTextArea",
          label: { text: "Description" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", height: 100 },
          validationRules: [
            { type: "required", message: "Description is required" },
          ],
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          itemType: "simple",
          name: "fileUpload",
          label: {
            text: this.mandatoryFiles ? "Upload Files *" : "Upload Files",
          },
          template: "fileUploadTemplate",
        },
        {
          // work around to bind file upload component to form and validate it
          dataField: "fileUploadProxy",
          colSpan: 2,
          itemType: "simple",
          label: { visible: false },
          editorType: "dxTextBox",
          editorOptions: {
            visible: false,
            value: this.files.length.toString(),
          },
          validationRules: [
            {
              type: "custom",
              message: "At least one file is required",
              validationCallback: (e) => this.onValidateFileAttachments(e),
              reevaluate: true,
            },
          ],
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          cssClass: "borrow-submit",
          editorType: "dxButton",
          editorOptions: { text: "Submit", useSubmitBehavior: true },
        },
      ];
      break;

    case "ADD BANK ACCOUNT TO INTERNAL SYSTEMS":
      this.mandatoryFiles = !this.isEditable;
      // Add CMA to accountTypes for this request type only
      if (!this.accountTypes.includes("CMA")) {
        this.accountTypes = [...this.accountTypes, "CMA"];
      }
      // Initialize reserveBuckets as an empty array for the TagBox
      if (!this.requestData.reserveBuckets) {
        this.requestData.reserveBuckets = [];
      }
      // Initialize accountType as an empty array for the TagBox
      if (!this.requestData.accountType) {
        this.requestData.accountType = [];
      }
      config = [
        {
          dataField: "requestType",
          editorType: "dxTextBox",
          label: { text: "Request Type" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { value: e.selectedItem.name, readOnly: true, required: "true" },
          validationRules: [
            { type: "required", message: "Request Type is required" },
          ],
        },
        {
          dataField: "accountName",
          editorType: "dxTextBox",
          label: { text: "Account Name" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", readOnly: false },
          validationRules: [
            { type: "required", message: "Account Name is required" },
          ],
        },
        {
          dataField: "accountNumber",
          editorType: "dxTextBox",
          label: { text: "Account Number" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", readOnly: false },
          validationRules: [
            { type: "required", message: "Account Number is required" },
          ],
        },
        {
          dataField: "bankName",
          editorType: "dxTextBox",
          label: { text: "Bank Name" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", readOnly: false },
          validationRules: [
            { type: "required", message: "Bank Name is required" },
          ],
        },
        {
          itemType: "group",
          colCount: 4,
          colSpan: 2,
          items: [
            {
              labelMode: "static",
              dataField: "accountType",
              editorType: "dxTagBox",
              colSpan: 4,
              cssClass: "dx-widget",
              label: { text: "Account Type" },
              editorOptions: {
                items: this.accountTypes,
                searchEnabled: true,
                showSelectionControls: true,
                applyValueMode: "useButtons",
                multiline: true,
                elementAttr: {
                  "data-testid": "account-type-tagbox"
                },
                onValueChanged: this.onAccountTypeChanged
              },
              validationRules: [
                { type: "required", message: "Account Type is required" },
              ],
            },
          ]
        },
        {
          dataField: "accountTypeDisplay",
          editorType: "dxTextBox",
          label: { text: "Selected Account Types" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: {
            readOnly: true,
            value: "",
            elementAttr: {
              "data-testid": "account-type-display"
            }
          },
        },
        {
          colSpan: 2,
          dataField: "accountNotOpenedByMs",
          editorType: "dxCheckBox",
          label: { text: "Account Not Opened By MS" },
          editorOptions: {
            text: "",
            onValueChanged: (e: any) => {
              this.accountNotOpenedByMs = e.value;
            }
          }
        },
        {
          itemType: "group",
          colCount: 4,
          colSpan: 2,
          items: [
            {
              labelMode: "static",
              dataField: "loanNumber",
              editorType: "dxSelectBox",
              colSpan: 3,
              cssClass: "dx-widget",
              label: { text: "Loan Number" },
              editorOptions: {
                  items: this.loanNumbers,
                  valueExpr: "name",
                  displayExpr: "name",
                  required: true,
                  searchMode: "contains",
                  searchEnabled: true,
                  acceptCustomValue: true,
                  onCustomItemCreating: (e) => {
                    const newItem = { name: e.text };
                    this.loanNumbers.push(newItem);
                    return newItem;
                  },
                  itemTemplate: this.loanItemTemplate,
                  onValueChanged: this.onLoanNumberChanged,
              },
              validationRules: [
                { type: "required", message: "Loan Number is required" },
              ],
            },
            {
              editorType: "dxCheckBox",
              label: { visible: false },
              cssClass: "loan-checkbox-center-align",
              editorOptions: {
                text: this.activeLoanCheckboxText,
                onValueChanged: this.onShowInactiveLoansChanged,
                value: this.showInactiveLoans,
              },
            },
          ],
        },
        {
          dataField: "dealName",
          editorType: "dxTextBox",
          label: { text: "Deal Name" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "false", defaultValue: "", readOnly: this.borrowerNameReadOnly },
        },
        {
          dataField: "borrowerName",
          editorType: "dxTextBox",
          label: { text: "Borrower Name" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "false", defaultValue: "", readOnly: this.borrowerNameReadOnly },
        },
        {
          colSpan: 2,
          dataField: "isComingled",
          editorType: "dxCheckBox",
          label: { text: "Is the Account Comingled?" },
          editorOptions: {
            text: "",
            onValueChanged: this.onIsComingledChanged
          }
        },
        {
          dataField: "reserveBuckets",
          editorType: "dxTagBox",
          label: { text: "Select the required Reserve Buckets" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: {
            items: this.reserveTypes,
            searchEnabled: true,
            showSelectionControls: true,
            applyValueMode: "useButtons",
            multiline: true,
            elementAttr: {
              "data-testid": "reserve-buckets-tagbox"
            },
            onValueChanged: this.onReserveBucketsChanged
          },
        },
        {
          dataField: "reserveBucketsDisplay",
          editorType: "dxTextBox",
          label: { text: "Selected Reserve Buckets" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: {
            readOnly: true,
            value: "",
            elementAttr: {
              "data-testid": "reserve-buckets-display"
            }
          },
        },
        {
          colSpan: 2,
          dataField: "isUrgent",
          editorType: "dxCheckBox",
          label: { text: "Rush Job" },
          editorOptions: {
            text: "",
            onValueChanged: (e: any) => {
              this.isUrgent = e.value;
            }
          }
        },
        {
          dataField: "description",
          editorType: "dxTextArea",
          label: { text: "Description" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", height: 100 },
          validationRules: [
            { type: "required", message: "Description is required" },
          ],
        },
        {
          itemType: "simple",
          colSpan: 2,
          template: (data, container) => {
            const label = document.createElement("div");
            label.style.textAlign = "center";
            label.style.fontWeight = "bold";
            label.style.padding = "10px";
            label.style.color = "#333";
            label.textContent = "CMA Agreement + Account Letter (or the documentation Asset Management has which documents the bank account name and number such as a screenshot of bank account from bank portal, email notating account number, etc.)";
            container.appendChild(label);
          }
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          itemType: "simple",
          name: "fileUpload",
          label: {
            text: this.mandatoryFiles ? "Upload Files *" : "Upload Files",
          },
          hint: "CMA Agreement + Account Letter (or the documentation Asset Management has which documents the bank account name and number such as a screenshot of bank account from bank portal, email notating account number, etc.)",
          template: "fileUploadTemplate",
        },
        {
          // work around to bind file upload component to form and validate it
          dataField: "fileUploadProxy",
          colSpan: 2,
          itemType: "simple",
          label: { visible: false },
          editorType: "dxTextBox",
          editorOptions: {
            visible: false,
            value: this.files.length.toString(),
          },
          validationRules: [
            {
              type: "custom",
              message: "At least one file is required",
              validationCallback: (e) => this.onValidateFileAttachments(e),
              reevaluate: true,
            },
          ],
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          cssClass: "borrow-submit",
          editorType: "dxButton",
          editorOptions: { text: "Submit", useSubmitBehavior: true },
        },
      ];
      break;

      case "LOAN MODIFICATION - CHANGE IN BANK ACCOUNT":
      this.mandatoryFiles = !this.isEditable;
      // Initialize accountType as an empty array for the TagBox
      if (!this.requestData.accountType) {
        this.requestData.accountType = [];
      }
      config = [
        {
          dataField: "requestType",
          editorType: "dxTextBox",
          label: { text: "Request Type" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { value: e.selectedItem.name, readOnly: true, required: "true" },
          validationRules: [
            { type: "required", message: "Request Type is required" },
          ],
        },
        {
          itemType: "group",
          colCount: 4,
          colSpan: 2,
          items: [
            {
              labelMode: "static",
              dataField: "loanNumber",
              editorType: "dxSelectBox",
              colSpan: 3,
              cssClass: "dx-widget",
              label: { text: "Loan Number" },
              editorOptions: {
                items: this.loanNumbers,
                valueExpr: "name",
                displayExpr: "name",
                required: true,
                searchMode: "contains",
                searchEnabled: true,
                acceptCustomValue: true,
                onCustomItemCreating: (e) => ({ name: e.text }),
                itemTemplate: this.loanItemTemplate,
                onValueChanged: this.onLoanNumberChanged,
              },
              validationRules: [
                { type: "required", message: "Loan Number is required" },
              ],
            },
            {
              editorType: "dxCheckBox",
              label: { visible: false },
              cssClass: "loan-checkbox-center-align",
              editorOptions: {
                text: this.activeLoanCheckboxText,
                onValueChanged: this.onShowInactiveLoansChanged,
                value: this.showInactiveLoans,
              },
            },
          ],
        },
        {
          itemType: "group",
          colCount: 4,
          colSpan: 2,
          items: [
            {
              labelMode: "static",
              dataField: "accountType",
              editorType: "dxTagBox",
              colSpan: 4,
              cssClass: "dx-widget",
              label: { text: "Account Type" },
              editorOptions: {
                items: this.accountTypes,
                searchEnabled: true,
                showSelectionControls: true,
                applyValueMode: "useButtons",
                multiline: true,
                elementAttr: {
                  "data-testid": "account-type-tagbox"
                },
                onValueChanged: this.onAccountTypeChanged
              },
              validationRules: [
                { type: "required", message: "Account Type is required" },
              ],
            },
          ]
        },
        {
          dataField: "accountTypeDisplay",
          editorType: "dxTextBox",
          label: { text: "Selected Account Types" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: {
            readOnly: true,
            value: "",
            elementAttr: {
              "data-testid": "account-type-display"
            }
          },
        },
        {
          dataField: "borrowerName",
          editorType: "dxTextBox",
          label: { text: "Borrower Name" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", readOnly: true },
        },
        {
          itemType: "group",
          colCount: 2,
          colSpan: 2,
          items: [
            {
              colSpan: 1,
              dataField: "isInterestBearing",
              editorType: "dxCheckBox",
              label: { text: "Interest Bearing Account Requested" },
              editorOptions: {
                text: "",
                onValueChanged: (e: any) => {
                  this.isInterestBearing = e.value;
                }
              }
            },
            {
              colSpan: 1,
              dataField: "accountNotOpenedByMs",
              editorType: "dxCheckBox",
              label: { text: "Account Not Opened By MS" },
              editorOptions: {
                text: "",
                onValueChanged: (e: any) => {
                  this.accountNotOpenedByMs = e.value;
                }
              }
            },
          ]
        },
        {
          colSpan: 2,
          dataField: "isUrgent",
          editorType: "dxCheckBox",
          label: { text: "Rush Job" },
          editorOptions: {
            text: "",
            onValueChanged: (e: any) => {
              this.isUrgent = e.value;
            }
          }
        },
        {
          dataField: "description",
          editorType: "dxTextArea",
          label: { text: "Description" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", height: 100 },
          validationRules: [
            { type: "required", message: "Description is required" },
          ],
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          itemType: "simple",
          name: "fileUpload",
          label: {
            text: this.mandatoryFiles ? "Upload Files *" : "Upload Files",
          },
          template: "fileUploadTemplate",
        },
        {
          // work around to bind file upload component to form and validate it
          dataField: "fileUploadProxy",
          colSpan: 2,
          itemType: "simple",
          label: { visible: false },
          editorType: "dxTextBox",
          editorOptions: {
            visible: false,
            value: this.files.length.toString(),
          },
          validationRules: [
            {
              type: "custom",
              message: "At least one file is required",
              validationCallback: (e) => this.onValidateFileAttachments(e),
              reevaluate: true,
            },
          ],
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          cssClass: "borrow-submit",
          editorType: "dxButton",
          editorOptions: { text: "Submit", useSubmitBehavior: true },
        },
      ];
      break;
    case "BANK ADMINISTRATION - ADD/CHANGE A USER":
    case "BANK FEE INQUIRY":

    case "BANK WIRING INSTRUCTION REQUEST":
      this.mandatoryFiles = false;
      config = [
        {
          dataField: "requestType",
          editorType: "dxTextBox",
          label: { text: "Request Type" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { value: e.selectedItem.name, readOnly: true, required: "true" },
          validationRules: [
            { type: "required", message: "Request Type is required" },
          ],
        },
        {
          itemType: "group",
          colCount: 4,
          colSpan: 2,
          items: [
            {
              labelMode: "static",
              dataField: "loanNumber",
              editorType: "dxSelectBox",
              colSpan: 3,
              cssClass: "dx-widget",
              label: { text: "Loan Number" },
              editorOptions: {
                items: this.loanNumbers,
                valueExpr: "name",
                displayExpr: "name",
                required: true,
                searchMode: "contains",
                searchEnabled: true,
                acceptCustomValue: true,
                onCustomItemCreating: (e) => ({ name: e.text }),
                itemTemplate: this.loanItemTemplate,
                onValueChanged: this.onLoanNumberChanged,
              },
              validationRules: [
                { type: "required", message: "Loan Number is required" },
              ],
            },
            {
              editorType: "dxCheckBox",
              label: { visible: false },
              cssClass: "loan-checkbox-center-align",
              editorOptions: {
                text: this.activeLoanCheckboxText,
                onValueChanged: this.onShowInactiveLoansChanged,
                value: this.showInactiveLoans,
              },
            },
          ],
        },
        {
          dataField: "borrowerName",
          editorType: "dxTextBox",
          label: { text: "Borrower Name" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", readOnly: true },
        },
        {
          itemType: "custom",
          name: "bankAccountsGrid",
          colSpan: 2,
          template: (formItemData, container) => this.bankAccountGridTemplate(formItemData, container)
        },
        {
          // work around to bind bank accounts grid to form and validate it
          dataField: "bankAccountsProxy",
          colSpan: 2,
          itemType: "simple",
          label: { visible: false },
          editorType: "dxTextBox",
          editorOptions: {
            visible: false,
            value: this.requestData.bankAccounts?.filter(a => a.accountName).length.toString() || "0",
          },
          validationRules: [
            {
              type: "custom",
              message: "At least one bank account is required",
              validationCallback: (e) => this.onValidatebankAccounts(e),
              reevaluate: true,
            },
          ],
        },
        {
          colSpan: 2,
          dataField: "accountNotOpenedByMs",
          editorType: "dxCheckBox",
          label: { text: "Account Not Opened By MS" },
          editorOptions: {
            text: "",
            onValueChanged: (e: any) => {
              this.accountNotOpenedByMs = e.value;
            }
          }
        },
        {
          colSpan: 2,
          dataField: "isUrgent",
          editorType: "dxCheckBox",
          label: { text: "Rush Job?" },
          editorOptions: {
            text: "",
            onValueChanged: (e: any) => {
              this.isUrgent = e.value;
            }
          }
        },
        {
          dataField: "description",
          editorType: "dxTextArea",
          label: { text: "Description" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", height: 100 },
          validationRules: [
            { type: "required", message: "Description is required" },
          ],
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          itemType: "simple",
          name: "fileUpload",
          label: {
            text: "Upload Files",
          },
          template: "fileUploadTemplate",
        },
        {
          // work around to bind file upload component to form and validate it
          dataField: "fileUploadProxy",
          colSpan: 2,
          itemType: "simple",
          label: { visible: false },
          editorType: "dxTextBox",
          editorOptions: {
            visible: false,
            value: this.files.length.toString(),
          },
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          cssClass: "borrow-submit",
          editorType: "dxButton",
          editorOptions: { text: "Submit", useSubmitBehavior: true },
        },
      ];
      break;
    case "BANK REQUEST - OTHER":
      this.mandatoryFiles = !this.isEditable;
      config = [
        {
          dataField: "requestType",
          editorType: "dxTextBox",
          label: { text: "Request Type" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { value: e.selectedItem.name, readOnly: true, required: "true" },
          validationRules: [
            { type: "required", message: "Request Type is required" },
          ],
        },
        {
          itemType: "group",
          colCount: 4,
          colSpan: 2,
          items: [
            {
              labelMode: "static",
              dataField: "loanNumber",
              editorType: "dxSelectBox",
              colSpan: 3,
              cssClass: "dx-widget",
              label: { text: "Loan Number" },
              editorOptions: {
                items: this.loanNumbers,
                valueExpr: "name",
                displayExpr: "name",
                required: true,
                searchMode: "contains",
                searchEnabled: true,
                acceptCustomValue: true,
                onCustomItemCreating: (e) => ({ name: e.text }),
                itemTemplate: this.loanItemTemplate,
                onValueChanged: this.onLoanNumberChanged,
              },
              validationRules: [
                { type: "required", message: "Loan Number is required" },
              ],
            },
            {
              editorType: "dxCheckBox",
              label: { visible: false },
              cssClass: "loan-checkbox-center-align",
              editorOptions: {
                text: this.activeLoanCheckboxText,
                onValueChanged: this.onShowInactiveLoansChanged,
                value: this.showInactiveLoans,
              },
            },
          ],
        },
        {
          dataField: "borrowerName",
          editorType: "dxTextBox",
          label: { text: "Borrower Name" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", readOnly: true },
        },
        {
          itemType: "custom",
          name: "bankAccountsGrid",
          colSpan: 2,
          template: (formItemData, container) => this.bankAccountGridTemplate(formItemData, container)
        },
        {
          // work around to bind bank accounts grid to form and validate it
          dataField: "bankAccountsProxy",
          colSpan: 2,
          itemType: "simple",
          label: { visible: false },
          editorType: "dxTextBox",
          editorOptions: {
            visible: false,
            value: this.requestData.bankAccounts?.filter(a => a.accountName).length.toString() || "0",
          },
          validationRules: [
            {
              type: "custom",
              message: "At least one bank account is required",
              validationCallback: (e) => this.onValidatebankAccounts(e),
              reevaluate: true,
            },
          ],
        },
        {
          colSpan: 2,
          dataField: "accountNotOpenedByMs",
          editorType: "dxCheckBox",
          label: { text: "Account Not Opened By MS" },
          editorOptions: {
            text: "",
            onValueChanged: (e: any) => {
              this.accountNotOpenedByMs = e.value;
            }
          }
        },
        {
          colSpan: 2,
          dataField: "isUrgent",
          editorType: "dxCheckBox",
          label: { text: "Rush Job?" },
          editorOptions: {
            text: "",
            onValueChanged: (e: any) => {
              this.isUrgent = e.value;
            }
          }
        },
        {
          dataField: "description",
          editorType: "dxTextArea",
          label: { text: "Description" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", height: 100 },
          validationRules: [
            { type: "required", message: "Description is required" },
          ],
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          itemType: "simple",
          name: "fileUpload",
          label: {
            text: "Upload Files",
          },
          template: "fileUploadTemplate",
        },
        {
          // work around to bind file upload component to form and validate it
          dataField: "fileUploadProxy",
          colSpan: 2,
          itemType: "simple",
          label: { visible: false },
          editorType: "dxTextBox",
          editorOptions: {
            visible: false,
            value: this.files.length.toString(),
          },
          validationRules: [
            {
              type: "custom",
              message: "At least one file is required",
              validationCallback: (e) => this.onValidateFileAttachments(e),
              reevaluate: true,
            },
          ],
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          cssClass: "borrow-submit",
          editorType: "dxButton",
          editorOptions: { text: "Submit", useSubmitBehavior: true },
        },
      ];
      break;
    case "SUBMIT PNC PAID EXH A":
      this.mandatoryFiles = !this.isEditable;
      config = [
        {
          dataField: "requestType",
          editorType: "dxTextBox",
          label: { text: "Request Type" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { value: e.selectedItem.name, readOnly: true, required: "true" },
          validationRules: [
            { type: "required", message: "Request Type is required" },
          ],
        },
        {
          itemType: "group",
          colCount: 4,
          colSpan: 2,
          items: [
            {
              labelMode: "static",
              dataField: "loanNumber",
              editorType: "dxSelectBox",
              colSpan: 3,
              cssClass: "dx-widget",
              label: { text: "Loan Number" },
              editorOptions: {
                items: this.loanNumbers,
                valueExpr: "name",
                displayExpr: "name",
                required: true,
                searchMode: "contains",
                searchEnabled: true,
                acceptCustomValue: true,
                onCustomItemCreating: (e) => ({ name: e.text }),
                itemTemplate: this.loanItemTemplate,
                onValueChanged: this.onLoanNumberChanged,
              },
              validationRules: [
                { type: "required", message: "Loan Number is required" },
              ],
            },
            {
              editorType: "dxCheckBox",
              label: { visible: false },
              cssClass: "loan-checkbox-center-align",
              editorOptions: {
                text: this.activeLoanCheckboxText,
                onValueChanged: this.onShowInactiveLoansChanged,
                value: this.showInactiveLoans,
              },
            },
          ],
        },
        {
          dataField: "borrowerName",
          editorType: "dxTextBox",
          label: { text: "Borrower Name" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", readOnly: true },
        },
        {
          itemType: "custom",
          name: "bankAccountsGrid",
          colSpan: 2,
          template: (formItemData, container) => this.bankAccountGridTemplate(formItemData, container)
        },
        {
          // work around to bind bank accounts grid to form and validate it
          dataField: "bankAccountsProxy",
          colSpan: 2,
          itemType: "simple",
          label: { visible: false },
          editorType: "dxTextBox",
          editorOptions: {
            visible: false,
            value: this.requestData.bankAccounts?.filter(a => a.accountName).length.toString() || "0",
          },
          validationRules: [
            {
              type: "custom",
              message: "At least one bank account is required",
              validationCallback: (e) => this.onValidatebankAccounts(e),
              reevaluate: true,
            },
          ],
        },
        {
          colSpan: 2,
          dataField: "isUrgent",
          editorType: "dxCheckBox",
          label: { text: "Rush Job?" },
          editorOptions: {
            text: "",
            onValueChanged: (e: any) => {
              this.isUrgent = e.value;
            }
          }
        },
        {
          dataField: "description",
          editorType: "dxTextArea",
          label: { text: "Description" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", height: 100 },
          validationRules: [
            { type: "required", message: "Description is required" },
          ],
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          itemType: "simple",
          name: "fileUpload",
          label: {
            text: "Upload Files",
          },
          template: "fileUploadTemplate",
        },
        {
          // work around to bind file upload component to form and validate it
          dataField: "fileUploadProxy",
          colSpan: 2,
          itemType: "simple",
          label: { visible: false },
          editorType: "dxTextBox",
          editorOptions: {
            visible: false,
            value: this.files.length.toString(),
          },
          validationRules: [
            {
              type: "custom",
              message: "At least one file is required",
              validationCallback: (e) => this.onValidateFileAttachments(e),
              reevaluate: true,
            },
          ],
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          cssClass: "borrow-submit",
          editorType: "dxButton",
          editorOptions: { text: "Submit", useSubmitBehavior: true },
        },
      ];
      break;
    case "TREASURY - OTHER":
      this.mandatoryFiles = !this.isEditable;
      config = [
        {
          dataField: "requestType",
          editorType: "dxTextBox",
          label: { text: "Request Type" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { value: e.selectedItem.name, readOnly: true, required: "true" },
          validationRules: [
            { type: "required", message: "Request Type is required" },
          ],
        },
        {
          itemType: "group",
          colCount: 4,
          colSpan: 2,
          items: [
            {
              labelMode: "static",
              dataField: "loanNumber",
              editorType: "dxSelectBox",
              colSpan: 3,
              cssClass: "dx-widget",
              label: { text: "Loan Number" },
              editorOptions: {
                items: this.loanNumbers,
                valueExpr: "name",
                displayExpr: "name",
                required: true,
                searchMode: "contains",
                searchEnabled: true,
                acceptCustomValue: true,
                onCustomItemCreating: (e) => ({ name: e.text }),
                itemTemplate: this.loanItemTemplate,
                onValueChanged: this.onLoanNumberChanged,
              },
              validationRules: [
                { type: "required", message: "Loan Number is required" },
              ],
            },
            {
              editorType: "dxCheckBox",
              label: { visible: false },
              cssClass: "loan-checkbox-center-align",
              editorOptions: {
                text: this.activeLoanCheckboxText,
                onValueChanged: this.onShowInactiveLoansChanged,
                value: this.showInactiveLoans,
              },
            },
          ],
        },
        {
          dataField: "borrowerName",
          editorType: "dxTextBox",
          label: { text: "Borrower Name" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", readOnly: true },
        },
        {
          itemType: "custom",
          name: "bankAccountsGrid",
          colSpan: 2,
          template: (formItemData, container) => this.bankAccountGridTemplate(formItemData, container, false)
        },
        {
          // work around to bind bank accounts grid to form and validate it
          dataField: "bankAccountsProxy",
          colSpan: 2,
          itemType: "simple",
          label: { visible: false },
          editorType: "dxTextBox",
          editorOptions: {
            visible: false,
            value: this.requestData.bankAccounts?.filter(a => a.accountName).length.toString() || "0",
          },
        },
        {
          colSpan: 2,
          dataField: "accountNotOpenedByMs",
          editorType: "dxCheckBox",
          label: { text: "Account Not Opened By MS" },
          editorOptions: {
            text: "",
            onValueChanged: (e: any) => {
              this.accountNotOpenedByMs = e.value;
            }
          }
        },
        {
          colSpan: 2,
          dataField: "isUrgent",
          editorType: "dxCheckBox",
          label: { text: "Rush Job?" },
          editorOptions: {
            text: "",
            onValueChanged: (e: any) => {
              this.isUrgent = e.value;
            }
          }
        },
        {
          dataField: "description",
          editorType: "dxTextArea",
          label: { text: "Description" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", height: 100 },
          validationRules: [
            { type: "required", message: "Description is required" },
          ],
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          itemType: "simple",
          name: "fileUpload",
          label: {
            text: this.mandatoryFiles ? "Upload Files" : "Upload Files",
          },
          template: "fileUploadTemplate",
        },
        {
          // work around to bind file upload component to form and validate it
          dataField: "fileUploadProxy",
          colSpan: 2,
          itemType: "simple",
          label: { visible: false },
          editorType: "dxTextBox",
          editorOptions: {
            visible: false,
            value: this.files.length.toString(),
          },
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          cssClass: "borrow-submit",
          editorType: "dxButton",
          editorOptions: { text: "Submit", useSubmitBehavior: true },
        },
      ];
      break;
      default:
      this.mandatoryFiles = !this.isEditable;
      config = [
        {
          dataField: "requestType",
          editorType: "dxTextBox",
          label: { text: "Request Type" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { value: e.selectedItem.name, readOnly: true, required: "true" },
          validationRules: [
            { type: "required", message: "Request Type is required" },
          ],
        },
        {
          itemType: "group",
          colCount: 4,
          colSpan: 2,
          items: [
            {
              labelMode: "static",
              dataField: "loanNumber",
              editorType: "dxSelectBox",
              colSpan: 3,
              cssClass: "dx-widget",
              label: { text: "Loan Number" },
              editorOptions: {
                items: this.loanNumbers,
                valueExpr: "name",
                displayExpr: "name",
                required: true,
                searchMode: "contains",
                searchEnabled: true,
                acceptCustomValue: true,
                onCustomItemCreating: (e) => ({ name: e.text }),
                itemTemplate: this.loanItemTemplate,
                onValueChanged: (e: any) => {
                  if (e.value && this.currentFormInstance) {
                    const loanBorrower = this.loanBorrowersSubject$.value.find(
                      lb => lb.loanNumber === e.value
                    );
                    const borrowerName = loanBorrower ? loanBorrower.borrower : '';
                    this.currentFormInstance.updateData('borrowerName', borrowerName);
                  }
                },
              },
              validationRules: [
                { type: "required", message: "Loan Number is required" },
              ],
            },
            {
              editorType: "dxCheckBox",
              label: { visible: false },
              cssClass: "loan-checkbox-center-align",
              editorOptions: {
                text: this.activeLoanCheckboxText,
                onValueChanged: this.onShowInactiveLoansChanged,
                value: this.showInactiveLoans,
              },
            },
          ],
        },
        {
          dataField: "description",
          editorType: "dxTextArea",
          label: { text: "Description" },
          colSpan: 2,
          cssClass: "dx-widget",
          editorOptions: { required: "true", defaultValue: "", height: 100 },
          validationRules: [
            { type: "required", message: "Description is required" },
          ],
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          itemType: "simple",
          name: "fileUpload",
          label: {
            text: this.mandatoryFiles ? "Upload Files *" : "Upload Files",
          },
          template: "fileUploadTemplate",
        },
        {
          // work around to bind file upload component to form and validate it
          dataField: "fileUploadProxy",
          colSpan: 2,
          itemType: "simple",
          label: { visible: false },
          editorType: "dxTextBox",
          editorOptions: {
            visible: false,
            value: this.files.length.toString(),
          },
          validationRules: [
            {
              type: "custom",
              message: "At least one file is required",
              validationCallback: (e) => this.onValidateFileAttachments(e),
              reevaluate: true,
            },
          ],
        },
        {
          colSpan: 2,
          horizontalAlignment: "center",
          cssClass: "borrow-submit",
          editorType: "dxButton",
          editorOptions: { text: "Submit", useSubmitBehavior: true },
        },
      ];
    }

    // console.log(JSON.stringify(config));
    this.formItemsBehaviourSubject$.next(config);
  }

  async onSubmit(e: any) {
    e.preventDefault();

    let validationResult = validationEngine.validateGroup("formGroup");

    if (validationResult.isValid) {
      try {
        let request: ITaskRequest = new TaskRequest();
        let requestType = this.requestTypes.find((t) => t.name == this.requestData.requestType);

        if (!requestType) {
          notify('Request type not found. Please select a valid request type.', 'error', getNotificationTimeout());
          return;
        }

        request.templateId = requestType.id;
        request.name = requestType.name;
        request.referenceName = this.requestData.loanNumber;
        request.referenceType = requestType.referenceType;

        // Extract all dataFields from the current form configuration
        const formDataFields = this.extractDataFieldsFromFormItems(this.formItems);

        // Check if bank accounts grid is present in the form
        const hasBankAccountsGrid = this.formItemsContainsBankAccountsGrid(this.formItems);

        // Build submission data with only fields present in the current form
        let submissionData: any = {};

        // Always include requestType
        submissionData.requestType = this.requestData.requestType;

        // Add fields that are in both requestData and current form configuration
        formDataFields.forEach(field => {
          if (this.requestData.hasOwnProperty(field) && field !== 'accountTypeDisplay') {

            submissionData[field] = this.requestData[field];
          }
        });

        // Special handling for bankAccounts grid if it's in the form
        if (hasBankAccountsGrid && this.requestData.bankAccounts) {
          // Filter out empty rows and format as "accountNumber - name" for known accounts, or just the name for custom entries
          const accountData = this.bankAccountsSubject$.value;
          submissionData.bankAccounts = this.requestData.bankAccounts
            .filter((row: any) => row.accountName && row.accountName.trim() !== '')
            .map((row: any) => {
              // Find the account details to get the accountNumber
              const account = accountData.find((a) => a.name === row.accountName);
              if (account && account.accountNumber) {
                return `${account.accountNumber} - ${account.name}`;
              }
              // For custom/free text entries, just return the name
              return row.accountName;
            });
        }

        // Add component-level boolean fields if they're in the form
        if (formDataFields.includes('isUrgent')) {
          submissionData.isUrgent = this.isUrgent;
        }
        if (formDataFields.includes('isInterestBearing')) {
          submissionData.isInterestBearing = this.isInterestBearing;
        }
        if (formDataFields.includes('isComingled')) {
          submissionData.isComingled = this.isComingled;
        }
        if (formDataFields.includes('accountNotOpenedByMs')) {
          submissionData.accountNotOpenedByMs = this.accountNotOpenedByMs;
        }

        // Convert reserveBuckets array to comma-separated string for submission
        if (formDataFields.includes('reserveBuckets') && this.requestData.reserveBuckets && Array.isArray(this.requestData.reserveBuckets)) {
          submissionData.reserveBuckets = this.requestData.reserveBuckets.join(', ');
        }

        // Convert accountType array to comma-separated string for submission
        if (formDataFields.includes('accountType') && this.requestData.accountType && Array.isArray(this.requestData.accountType)) {
          submissionData.accountType = this.requestData.accountType.join(', ');
        }

        request.requestJsonData = this.appendDealNameToSubmission(submissionData);
        let formData: FormData = new FormData()
        formData.append('templateId', request.templateId);
        formData.append('locations', request.locations);
        formData.append('name', request.name);
        formData.append('referenceName', request.referenceName);
        formData.append(
          "requestJsonData",
          this.removeProxyData(JSON.stringify(request.requestJsonData)),
        );
        formData.append('referenceType', request.referenceType);
        formData.append('isUrgent', this.isUrgent.toString());
        if (formDataFields.includes('isComingled')) {
          formData.append('isComingled', this.isComingled.toString());
        }
        if(this.isEditable)
            formData.append('parentId', this.task.parentId);

        this.files.forEach((f) => { formData.append('files', f, f.name) });

        this.isLoading = true;

        const response = await lastValueFrom(this.taskDataService.insertTaskAsFormData(formData));
        sendNotificationMessage(`${response[1].data[0].task.taskId} task created`, this.messageService);

        this.isLoading = false;
        this.formElement.nativeElement.reset();
        this.formElement.nativeElement.submit();
      }
      catch (err: any) {
        this.isLoading = false;
        this.loggingService.logError(err, SeverityLevel.Error);
        notify(err.message, 'error', getNotificationTimeout());
      }
    }
  }



  override async ngOnInit() {
    try {
      this.isUrgent = false;
      let requestData =  await lastValueFrom( this.taskDataService.getTaskTemplatesByType(this.taskTemplateName));
      this.requestTypes = requestData[1];

      this.bankData = await lastValueFrom(this.treasuryDataService.getBanks());
      const reserveTypesResponse = await lastValueFrom(this.taskDataService.getReserveTypes(''));
      const rawReserveTypes = reserveTypesResponse[1] || [];
      // Format all caps reserve types to title case
      this.reserveTypes = rawReserveTypes.map((type: string) => {
        if (type && typeof type === 'string' && type === type.toUpperCase()) {
          return type.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
        }
        return type;
      });
      // Load bank accounts asynchronously - the BehaviorSubject will notify subscribers when data arrives
      this.treasuryDataService.getBankAccounts().subscribe({
        next: (accountData) => {
          const bankAccounts = accountData;
          this.bankAccountsSubject$.next(bankAccounts);
        },
        error: (err) => {
          this.loggingService.logError(err, SeverityLevel.Error);
          notify("Failed to load bank accounts", "error", getNotificationTimeout());
        }
      });

      // Load loan borrowers asynchronously
      this.taskDataService.getLoanBorrowers().subscribe({
        next: (loanBorrowersResponse) => {
          const borrowers = (loanBorrowersResponse as any)[1];
          this.loanBorrowersSubject$.next(borrowers);
        },
        error: (err) => {
          this.loggingService.logError(err, SeverityLevel.Error);
          console.error('Failed to load loan borrowers:', err);
        }
      });
      await super.ngOnInit();

      this.isEditable = this.task != null ? true : false;
      if(this.isEditable)
      {
        this.treasuryRequestData = FormUtils.mapFromTask<TreasuryRequestData>(this.task, TreasuryRequestData);
        console.log(this.treasuryRequestData);

        const parsedData = this.treasuryRequestData as any;

        // Copy all parsed task data into requestData, which is the dx-form's [formData] binding
        this.requestData = { ...parsedData };

        // Convert bankAccounts from saved "accountNumber - name" strings back to grid row objects
        if (Array.isArray(parsedData.bankAccounts) && parsedData.bankAccounts.length > 0) {
          this.requestData.bankAccounts = parsedData.bankAccounts.map((entry: string) => {
            const separatorIndex = entry.indexOf(' - ');
            const accountName = separatorIndex >= 0 ? entry.substring(separatorIndex + 3) : entry;
            return { key: this.generateUniqueId(), accountName };
          });
        }

        // Convert accountType from comma-separated string back to array for the TagBox editor
        if (typeof parsedData.accountType === 'string' && parsedData.accountType) {
          this.requestData.accountType = parsedData.accountType.split(', ').map((s: string) => s.trim());
          // Rebuild the display field (excluded from saved task data) so it renders correctly
          this.requestData.accountTypeDisplay = this.requestData.accountType.join(', ');
        }

        // Convert reserveBuckets from comma-separated string back to array for the TagBox editor
        if (typeof parsedData.reserveBuckets === 'string' && parsedData.reserveBuckets) {
          this.requestData.reserveBuckets = parsedData.reserveBuckets.split(', ').map((s: string) => s.trim());
          // Rebuild the display field so it renders correctly
          this.requestData.reserveBucketsDisplay = this.requestData.reserveBuckets.join(', ');
        }

        // Sync component-level boolean flags so submit logic and form config use correct initial values
        this.isUrgent = parsedData.isUrgent || false;
        this.isComingled = parsedData.isComingled || false;
        this.isInterestBearing = parsedData.isInterestBearing || false;
        this.accountNotOpenedByMs = parsedData.accountNotOpenedByMs || false;

        // For editing, we need to programmatically trigger form configuration
        // since onRequestTypeSelectionChanged is only called on user interaction
        if (this.treasuryRequestData.requestType) {
          const requestTypeObj = this.requestTypes.find(rt => rt.name === this.treasuryRequestData.requestType);
          if (requestTypeObj) {
            // Simulate the selection event to create the form configuration
            this.onRequestTypeSelectionChanged({
              value: this.treasuryRequestData.requestType,
              selectedItem: { name: this.treasuryRequestData.requestType }
            });
            // The config items have hardcoded editorOptions.value overrides (e.g. value: "").
            // Patch them now that formItems has been populated by onRequestTypeSelectionChanged.
            this.updateLoanDataInFormConfig();
          }
        }
      }
      else{
        this.treasuryRequestData = new TreasuryRequestData();
      }

    }
    catch (err: any) {
      this.loggingService.logError(err, SeverityLevel.Error);
      this.isLoading = false;
      notify(err.message, 'error', getNotificationTimeout());
    }
  }

    // Add this method to generate unique IDs
  private generateUniqueId(): string {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Adds an empty row to the bank accounts grid
   */
  private addEmptyRowToGrid(gridInstance: any): void {
    const newRow = {
      key: this.generateUniqueId(),
      accountName: null,
    };

    // Add the row to the data source
    this.requestData.bankAccounts.push(newRow);

    // Refresh the grid to show the new row
    gridInstance.refresh();
  }

  /**
   * Removes empty rows from bank accounts before submission and converts to array of "accountNumber - name" format
   */
  private cleanupbankAccounts(): void {
    if (this.requestData.bankAccounts) {
      const accountData = this.bankAccountsSubject$.value;

      // Filter out empty rows and format as "accountNumber - name"
      this.requestData.bankAccounts = this.requestData.bankAccounts
        .filter((row) => row.accountName && row.accountName.trim() !== '')
        .map((row) => {
          // Find the account details to get the accountNumber
          const account = accountData.find((a) => a.name === row.accountName);
          if (account && account.accountNumber) {
            return `${account.accountNumber} - ${account.name}`;
          }
          // Fallback to just the name if account details not found
          return row.accountName;
        });
    }
  }

  bankAccountGridTemplate(formItemData, container, isRequired: boolean = true) {

    // Create main container
    const mainContainer = document.createElement("div");
    mainContainer.style.position = "relative";
    container.appendChild(mainContainer);
    // Create grid container
    const gridElement = document.createElement("div");
    gridElement.style.marginTop = "0px"; // No extra margin needed
    gridElement.setAttribute("data-testid", "bank-account-grid");
    mainContainer.appendChild(gridElement);

    // Initialize the data source if it doesn't exist
    if (!this.requestData.bankAccounts) {
      this.requestData.bankAccounts = [];
    }

    // Flag to prevent onContentReady from adding multiple rows
    let isGridInitialized = false;

    const grid = new dxDataGrid(gridElement, {
      dataSource: this.requestData.bankAccounts, // Use direct array reference
      keyExpr: "key", // Use keyExpr instead of store configuration
      columns: [
        {
          dataField: "accountName",
          caption: isRequired ? "Bank Accounts *" : "Bank Accounts",
          dataType: "string",
          lookup: {
            // Use a function that returns the observable's current value
            dataSource: () => this.bankAccountsSubject$.value,
            valueExpr: "name",
            displayExpr: (item) => {
              if (item?.name && item?.accountNumber ) {
                return `${item.accountNumber} - ${item.name}`;
              }
              return item?.name || item;
            },
          },
          cellTemplate: (container, options) => {
            const accountData = this.bankAccountsSubject$.value;
            const account = accountData.find(
              (a) => a.name === options.value,
            );
            const textStyle: Partial<CSSStyleDeclaration> = {
              color: 'inherit',
              fontWeight: 'normal',
              wordWrap: 'break-word',
              whiteSpace: 'normal',
            };
            if (account) {
              const displayText = `${account.accountNumber} - ${account.name}`;
              UtilitiesService.renderText(container, displayText, { tag: 'span', style: textStyle });
            } else if (options.value) {
              // Display custom/free text entry (no account number)
              UtilitiesService.renderText(container, options.value, { tag: 'span', style: textStyle });
            } else {
              // Show placeholder text when cell is empty
              UtilitiesService.renderText(container, 'Click to add account', {
                tag: 'span',
                style: { color: '#999', fontStyle: 'italic' },
              });
            }
            // Set minimum height on cell to prevent jumping
            container.style.minHeight = "36px";
            container.style.display = "flex";
            container.style.alignItems = "center";
          },
          // Remove validation rules - we'll validate on form submit instead
        },
      ],
      wordWrapEnabled: true,
      repaintChangesOnly: true,
      editing: {
        mode: "cell",
        allowAdding: false,
        allowUpdating: true,
        allowDeleting: true,
        confirmDelete: false,
        selectTextOnEditStart: true,
        startEditAction: "click",
      },
      onRowRemoved: (e) => {
        // Row has been deleted, ensure there's at least one empty row
        const hasEmptyRow = this.requestData.bankAccounts.some(
          (item) => !item.accountName
        );
        if (!hasEmptyRow || this.requestData.bankAccounts.length === 0) {
          this.addEmptyRowToGrid(e.component);
        }

        // Update form validation
        this.updatebankAccountsValidation();
      },
      toolbar: {
        visible: false, // Hide toolbar completely
      },
      showBorders: true,
      columnAutoWidth: true,
      rowAlternationEnabled: true,
      onCellClick: (e) => {
        // Start editing immediately on any cell click, even if grid is not focused
        if (e.rowType === "data" && e.column.dataField === "accountName") {
          e.component.editCell(e.rowIndex, e.columnIndex);
        }
      },
      onContentReady: (e) => {
        // Add initial empty row only once when grid is first ready
        if (!isGridInitialized) {
          const dataSource = e.component.getDataSource();
          if (dataSource && dataSource.items().length === 0) {
            this.addEmptyRowToGrid(e.component);
          }
          isGridInitialized = true;
        }
      },
      onEditorPreparing: (e) => {
        if (e.dataField === "accountName" && e.parentType === "dataRow") {
          // Add placeholder text
          e.editorOptions.placeholder = "Click to add account";

          // Allow free text entry in addition to selecting from list
          e.editorOptions.acceptCustomValue = true;

          // Filter out already selected accounts
          const allAccounts = this.bankAccountsSubject$.value;
          const selectedAccounts = this.requestData.bankAccounts
            .filter(row => row.accountName && row.key !== e.row.data.key) // Exclude current row being edited
            .map(row => row.accountName);

          // Only show accounts that haven't been selected yet
          const availableAccounts = allAccounts.filter(account =>
            !selectedAccounts.includes(account.name)
          );

          // Update the lookup data source with filtered accounts
          e.editorOptions.dataSource = availableAccounts;

          // Handle custom value creation (when user enters free text)
          e.editorOptions.onCustomItemCreating = (args) => {
            // Return the custom text as a simple object that matches the expected structure
            return { name: args.text, accountNumber: null };
          };

          // Use onInitialized to open dropdown immediately after editor is created
          const originalOnInitialized = e.editorOptions.onInitialized;
          e.editorOptions.onInitialized = (args) => {
            if (originalOnInitialized) {
              originalOnInitialized(args);
            }
            // Open the dropdown immediately
            setTimeout(() => {
              args.component.open();
            }, 0);
          };

          // When the lookup editor is shown, handle value change immediately
          e.editorOptions.onValueChanged = (args) => {
            if (args.value) {
              // console.log('Value selected:', args.value);

              // Just set the value and let DevExtreme handle the rest
              e.setValue(args.value);

              // Close the cell after a delay
              setTimeout(() => {
                e.component.closeEditCell();
              }, 50);
            }
          };
        }
      },
      onRowUpdated: (e) => {
        // console.log('onRowUpdated fired, e.data:', e.data);
        // console.log('Array BEFORE manual update:', JSON.stringify(this.requestData.bankAccounts));

        // The array is being cleared by DevExtreme, so we need to reconstruct it from the grid
        if (e.data && e.data.accountName) {
          // Find if this row already exists in the array
          const existingRowIndex = this.requestData.bankAccounts.findIndex(row => row.key === e.data.key);

          if (existingRowIndex >= 0) {
            // Update existing row
            this.requestData.bankAccounts[existingRowIndex].accountName = e.data.accountName;
            // console.log('Updated existing row at index', existingRowIndex);
          } else {
            // Add the row from e.data since array is empty or doesn't contain it
            this.requestData.bankAccounts.push({
              key: e.data.key,
              accountName: e.data.accountName
            });
            // console.log('Added new row to array from e.data');
          }

          // Auto-populate fields for REMOVE BANK ACCOUNT FROM INTERNAL SYSTEMS
          if (this.requestData.requestType?.toUpperCase() === "REMOVE BANK ACCOUNT FROM INTERNAL SYSTEMS") {
            // Get the first selected bank account
            const selectedAccountName = e.data.accountName;
            const allAccounts = this.bankAccountsSubject$.value;
            const selectedAccount = allAccounts.find(acc => acc.name === selectedAccountName);

            if (selectedAccount && this.currentFormInstance) {
              // Update the form fields with the selected account details
              this.currentFormInstance.updateData('accountName', selectedAccount.name || '');
              this.currentFormInstance.updateData('accountNumber', selectedAccount.accountNumber || '');
              this.currentFormInstance.updateData('bankName', selectedAccount.bank?.name || '');

              // Also update the requestData object
              this.requestData.accountName = selectedAccount.name || '';
              this.requestData.accountNumber = selectedAccount.accountNumber || '';
              this.requestData.bankName = selectedAccount.bank?.name || '';
            }
          }
        }

        // console.log('Array AFTER manual update:', JSON.stringify(this.requestData.bankAccounts));

        this.isGridInEditMode = false;

        // Check if we need to add a new empty row
        setTimeout(() => {
          const hasEmptyRow = this.requestData.bankAccounts.some(
            (item) => !item.accountName || item.accountName.trim() === ''
          );
          if (!hasEmptyRow) {
            this.addEmptyRowToGrid(e.component);
          }

          // Update validation AFTER everything is done
          this.updatebankAccountsValidation();
        }, 100);
      },
      onEditingStart: (e) => {
        this.isGridInEditMode = true;
      },
      onEditCanceled: (e) => {
        this.isGridInEditMode = false;
      },
    });

    // Store grid reference for later lookup updates
    this.bankAccountsGrid = grid;

    // Subscribe to bank accounts changes and refresh the grid lookup when data arrives
    this.bankAccounts$.subscribe((accounts) => {
      if (accounts && accounts.length > 0 && grid) {
        // Update the lookup data source
        grid.columnOption("accountName", "lookup.dataSource", accounts);
        grid.repaint();
      }
    });

    return grid;
  }


  onValidateFileAttachments(e: any): boolean {
    if (this.mandatoryFiles && this.files.length == 0) {
      e.isValid = false;
      return false;
    }
    e.isValid = true;
    return true;
  }

  onValidatebankAccounts(e: any): boolean {
    // Check if we have valid bank accounts without cleaning them up during validation
    let hasValidAccounts = false;

    if (this.requestData.bankAccounts && this.requestData.bankAccounts.length > 0) {
      hasValidAccounts = this.requestData.bankAccounts.some(account => {
        // Handle both object format (from grid) and string format (after cleanup)
        if (typeof account === 'string') {
          return account && account.trim() !== '';
        } else if (typeof account === 'object' && account !== null) {
          return account.accountName && account.accountName.trim() !== '';
        }
        return false;
      });
    }

    if (!hasValidAccounts) {
      e.isValid = false;
      return false;
    }
    e.isValid = true;
    return true;
  }

  updatebankAccountsValidation(): void {
    // Reset the validation state by re-validating the entire form group
    // This will trigger the validationCallback which checks the actual data
    if (this.currentFormInstance) {
      try {
        // Get the validator instance for the proxy field
        const proxyEditor = this.currentFormInstance.getEditor('bankAccountsProxy');
        if (proxyEditor) {
          // Reset validation state of the proxy field to clear any error messages
          proxyEditor.reset();

          // Count valid bank accounts for logging
          let bankAccountsCount = 0;
          // console.log('Raw bankAccounts array:', JSON.stringify(this.requestData.bankAccounts));

          if (this.requestData.bankAccounts) {
            const validAccounts = this.requestData.bankAccounts.filter(acc => {
              if (typeof acc === 'string') {
                return acc && acc.trim() !== '';
              } else if (typeof acc === 'object' && acc !== null) {
                return acc.accountName && acc.accountName.trim() !== '';
              }
              return false;
            });
            bankAccountsCount = validAccounts.length;
            // console.log('Valid accounts:', JSON.stringify(validAccounts));
          }

          // console.log('bank accounts validation reset, count:', bankAccountsCount);
        }
      } catch (error) {
        // console.log('Could not update bank accounts validation:', error);
      }
    }
  }
  onFormContentReady(e: any): void {
    this.currentFormInstance = e.component;
    if (this.isEditable) {
      if (this.requestData.accountTypeDisplay) {
        this.currentFormInstance.updateData('accountTypeDisplay', this.requestData.accountTypeDisplay);
      }
      if (this.requestData.reserveBucketsDisplay) {
        this.currentFormInstance.updateData('reserveBucketsDisplay', this.requestData.reserveBucketsDisplay);
      }
    }
  }

  onFieldDataChanged(e: any): void {
    // Note: loanNumber changes are handled by onLoanNumberChanged callback
    // configured in the form items editorOptions.onValueChanged
    // This prevents conflicts with custom loan number logic
  }

  private setFieldReadOnlyState(dataField: string, isReadOnly: boolean): void {
    if (!this.currentFormInstance) {
      return;
    }

    if (dataField === 'borrowerName') {
      if (this.borrowerNameReadOnly === isReadOnly) {
        // No change, avoid extra work
      } else {
        this.borrowerNameReadOnly = isReadOnly;
      }
    } else if (dataField === 'dealName') {
      if (this.dealNameReadOnly === isReadOnly) {
        // No change
      } else {
        this.dealNameReadOnly = isReadOnly;
      }
    }

    // Keep the backing config in sync so future re-renders preserve the state
    this.updateFormItemReadOnlyState(this.formItems, dataField, isReadOnly);

    try {
      const itemOption = this.currentFormInstance.itemOption(dataField);
      if (itemOption) {
        const updatedEditorOptions = {
          ...(itemOption.editorOptions || {}),
          readOnly: isReadOnly,
          disabled: false,
        };
        this.currentFormInstance.itemOption(dataField, 'editorOptions', updatedEditorOptions);
      }
    } catch (err) {
      console.warn(`Unable to update itemOption for ${dataField}:`, err);
    }

    const editor: any = this.currentFormInstance.getEditor(dataField);
    if (!editor) {
      console.warn(`Editor instance for ${dataField} not found when setting readOnly=${isReadOnly}`);
      return;
    }

    editor.option('readOnly', isReadOnly);
    editor.option('disabled', false);
    editor.repaint();

    let editorElement: HTMLElement | null = null;
    if (typeof editor.element === 'function') {
      const elementResult = editor.element();
      if (Array.isArray(elementResult)) {
        editorElement = elementResult[0] ?? null;
      } else if (elementResult && typeof elementResult.get === 'function') {
        editorElement = elementResult.get(0);
      } else {
        editorElement = elementResult as HTMLElement;
      }
    }

    if (editorElement) {
      editorElement.classList.toggle('dx-state-readonly', isReadOnly);
      editorElement.classList.toggle('dx-state-disabled', false);
      const inputEl = editorElement.querySelector('input');
      if (inputEl) {
        if (isReadOnly) {
          inputEl.setAttribute('readonly', 'readonly');
          inputEl.classList.add('dx-texteditor-input-readonly');
        } else {
          inputEl.removeAttribute('readonly');
          inputEl.classList.remove('dx-texteditor-input-readonly');
          inputEl.tabIndex = 0;
        }
      }
    }
  }

  private updateFormItemReadOnlyState(items: any[], dataField: string, isReadOnly: boolean): boolean {
    if (!items || !Array.isArray(items)) {
      return false;
    }

    for (const item of items) {
      if (item.dataField === dataField) {
        item.editorOptions = item.editorOptions || {};
        item.editorOptions.readOnly = isReadOnly;
        return true;
      }

      if (item.items && this.updateFormItemReadOnlyState(item.items, dataField, isReadOnly)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Recursively extracts all dataField values from form items configuration
   */
  private extractDataFieldsFromFormItems(items: any[]): string[] {
    const dataFields: string[] = [];

    if (!items || !Array.isArray(items)) {
      return dataFields;
    }

    for (const item of items) {
      // Add dataField if it exists and is not a proxy field
      if (item.dataField && !item.dataField.endsWith('Proxy')) {
        dataFields.push(item.dataField);
      }

      // Recursively process nested items (for groups)
      if (item.items && Array.isArray(item.items)) {
        dataFields.push(...this.extractDataFieldsFromFormItems(item.items));
      }
    }

    return dataFields;
  }

  /**
   * Checks if the form contains the bank accounts grid custom template
   */
  private formItemsContainsBankAccountsGrid(items: any[]): boolean {
    if (!items || !Array.isArray(items)) {
      return false;
    }

    for (const item of items) {
      // Check if this is the bank accounts grid
      if (item.itemType === 'custom' && item.name === 'bankAccountsGrid') {
        return true;
      }

      // Recursively check nested items (for groups)
      if (item.items && Array.isArray(item.items)) {
        if (this.formItemsContainsBankAccountsGrid(item.items)) {
          return true;
        }
      }
    }

    return false;
  }
  removeProxyData(data: string): string {
    if(data) {
      let json = JSON.parse(data);
      delete json["fileUploadProxy"];
      delete json["bankAccountsProxy"];
      delete json["reserveBucketsDisplay"];
      return JSON.stringify(json);
    }
    return "{}";
  }
}
